import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { ResponseDocumentListItem, ResponseDocumentVersion } from '@/schema'
import { SortOrder } from '@/types/common'

import {
  DocumentFilters,
  DocumentFilterType,
  IFocusSearchResult,
} from './types'

// Document store state and actions
interface DocumentStore {
  // State
  // Delete document(s)
  isDeleteMultiple: boolean
  deleteMultipleDocumentIds: string[]
  deleteDialogOpen: boolean

  // Searching
  searchText: string
  isSearchResultsExists: boolean
  focusSearchResult: IFocusSearchResult | null
  toggleNextFocus: boolean

  // Open document(s)
  openDocuments: { id: string; filename: string }[]
  activeDocumentId: string | null
  versionIdByDocumentId: Record<string, string>
  listVersionByDocumentId: Record<string, ResponseDocumentVersion[]>

  // Actions
  // Delete document(s)
  setIsDeleteMultiple: (isDeleteMultiple: boolean) => void
  setDeleteMultipleDocumentIds: (ids: string[]) => void
  setDeleteDialogOpen: (open: boolean) => void
  clearDeletingDocument: () => void

  // Searching
  setSearchText: (searchText: string) => void
  setIsSearchResultsExists: (isSearchResultsExists: boolean) => void
  setFocusSearchResult: (result: IFocusSearchResult) => void
  setToggleNextFocus: () => void

  // Open document(s)
  setOpenDocument: (documents: { id: string; filename: string }[]) => void
  setActiveDocumentId: (id: string | null) => void
  clearOpenDocuments: () => void

  // Listing
  // State
  filters: DocumentFilters
  page: number
  sort: {
    column: keyof ResponseDocumentListItem
    direction: SortOrder
  }
  deletingDocument: ResponseDocumentListItem | null
  isLoadingTOC: boolean
  tocError: Error | null
  onlyOpenComments: boolean
  showBookmarked: boolean

  // Actions
  setFilter: (payload: Partial<DocumentFilters>) => void
  setPage: (page: number) => void
  setOnlyOpenComments: (value: boolean) => void
  setShowBookmarked: (value: boolean) => void
  setVersionId: (documentId: string, versionId: string | null) => void
  setListVersion: (
    documentId: string,
    listVersion: ResponseDocumentVersion[]
  ) => void
  resetListVersion: () => void
  getVersionId: (documentId: string) => string | null
  getListVersion: (documentId: string) => ResponseDocumentVersion[]
  getIsLatestVersion: (documentId: string) => boolean
  setSort: (sort: {
    column: keyof ResponseDocumentListItem | null
    direction: SortOrder
  }) => void
  setDeletingDocument: (document: ResponseDocumentListItem | null) => void
  setLoadingTOC: (loading: boolean) => void
  setTOCError: (error: Error | null) => void
  reset: () => void
}

// Initial state
const initialState = {
  isDeleteMultiple: false,
  deleteMultipleDocumentIds: [] as string[],
  deleteDialogOpen: false,

  searchText: '',
  isSearchResultsExists: false,
  focusSearchResult: null,
  toggleNextFocus: false,

  openDocuments: [] as { id: string; filename: string }[],
  activeDocumentId: null as string | null,
  versionIdByDocumentId: {} as Record<string, string>,
  listVersionByDocumentId: {} as Record<string, ResponseDocumentVersion[]>,
  // Listing
  filters: {
    searchName: '',
    statuses: [],
    type: 'All' as DocumentFilterType,
  },
  page: 0,
  sort: {
    column: 'created_at' as keyof ResponseDocumentListItem,
    direction: 'desc' as SortOrder,
  },
  deletingDocument: null as ResponseDocumentListItem | null,
  isLoadingTOC: false,
  tocError: null as Error | null,
  onlyOpenComments: false,
  showBookmarked: false,
}

export const getDefaultVersionId = (
  versions: ResponseDocumentVersion[]
): string | null => {
  if (versions.length === 0) return null
  return versions[0].id
}

/**
 * Document Store
 *
 * @example
 * ```tsx
 * // In a component
 * const { filters, setFilter } = useDocumentStore()
 *
 * // Set filter
 * setFilter({ searchName: 'test' })
 * ```
 */
export const useDocumentStore = create<DocumentStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      /**
       * Set search text
       */
      setSearchText: (searchText: string) => {
        set({ searchText, focusSearchResult: null }, false, 'setSearchText')
      },

      /**
       * Set whether search results exist
       */
      setIsSearchResultsExists: (isSearchResultsExists: boolean) => {
        set({ isSearchResultsExists }, false, 'setIsSearchResultsExists')
      },

      /**
       * Set the focus search result
       */
      setFocusSearchResult: (result: IFocusSearchResult) => {
        set({ focusSearchResult: result }, false, 'setFocusSearchResult')
      },

      /**
       * Set whether to toggle next focus
       */
      setToggleNextFocus: () => {
        const { toggleNextFocus } = get()

        set({ toggleNextFocus: !toggleNextFocus }, false, 'setToggleNextFocus')
      },

      /**
       * Set whether to delete multiple documents
       */
      setIsDeleteMultiple: (isDeleteMultiple: boolean) => {
        set(
          { isDeleteMultiple: isDeleteMultiple },
          false,
          'setIsDeleteMultiple'
        )
      },
      /**
       * Set the document IDs being deleted
       */
      setDeleteMultipleDocumentIds: (ids: string[]) => {
        set(
          { deleteMultipleDocumentIds: ids },
          false,
          'setDeleteMultipleDocumentIds'
        )
      },

      /**
       * Set delete dialog open/close state
       */
      setDeleteDialogOpen: (open: boolean) => {
        set({ deleteDialogOpen: open }, false, 'setDeleteDialogOpen')
      },

      /**
       * Clear the deleting document
       */
      clearDeletingDocument: () => {
        set(
          {
            deleteDialogOpen: false,
            isDeleteMultiple: false,
            deleteMultipleDocumentIds: [],
          },
          false,
          'clearDeletingDocument'
        )
      },

      /**
       * Set the document being opened
       * If id is not in the list, add it, otherwise remove it
       */
      setOpenDocument: (documents: { id: string; filename: string }[]) => {
        set({ openDocuments: documents }, false, 'setOpenDocument')
      },

      /**
       * Set the active document ID
       */
      setActiveDocumentId: (id: string) => {
        set({ activeDocumentId: id }, false, 'setActiveDocumentId')
      },

      setVersionId: (documentId: string, versionId: string | null) => {
        const { versionIdByDocumentId } = get()
        if (versionId) {
          if (versionIdByDocumentId[documentId] === versionId) return
          set(
            {
              versionIdByDocumentId: {
                ...versionIdByDocumentId,
                [documentId]: versionId,
              },
            },
            false,
            'setVersionId'
          )
          return
        }
        if (!(documentId in versionIdByDocumentId)) return
        const { [documentId]: _, ...rest } = versionIdByDocumentId
        set({ versionIdByDocumentId: rest }, false, 'setVersionId')
      },

      setListVersion: (
        documentId: string,
        listVersion: ResponseDocumentVersion[]
      ) => {
        const { listVersionByDocumentId } = get()
        set(
          {
            listVersionByDocumentId: {
              ...listVersionByDocumentId,
              [documentId]: listVersion,
            },
          },
          false,
          'setListVersion'
        )
      },
      resetListVersion: () => {
        set({ listVersionByDocumentId: {} }, false, 'resetListVersion')
        set({ versionIdByDocumentId: {} }, false, 'resetListVersion')
      },

      getVersionId: (documentId: string) => {
        const { versionIdByDocumentId } = get()
        return versionIdByDocumentId[documentId] ?? null
      },

      getListVersion: (documentId: string) => {
        const { listVersionByDocumentId } = get()
        return listVersionByDocumentId[documentId] ?? []
      },

      getIsLatestVersion: (documentId: string) => {
        const { versionIdByDocumentId, listVersionByDocumentId } = get()
        const versionId = versionIdByDocumentId[documentId]
        const listVersion = listVersionByDocumentId[documentId] ?? []
        if (!listVersion.length || !versionId) return true
        return versionId === listVersion[0]?.id
      },
      /**
       * Clear the opened documents
       */
      clearOpenDocuments: () => {
        set(
          { openDocuments: [], activeDocumentId: null },
          false,
          'clearOpenDocuments'
        )
      },

      /**
       * Set a filter value
       */
      setFilter: (payload: Partial<DocumentFilters>) => {
        set(
          { filters: { ...initialState.filters, ...payload } },
          false,
          'setFilter'
        )
      },

      /**
       * Set current page
       */
      setPage: (page: number) => {
        set({ page }, false, 'setPage')
      },

      /**
       * Set only open comments filter
       */
      setOnlyOpenComments: (value: boolean) => {
        set({ onlyOpenComments: value }, false, 'setOnlyOpenComments')
      },

      /**
       * Set show bookmarked filter
       */
      setShowBookmarked: (value: boolean) => {
        set({ showBookmarked: value }, false, 'setShowBookmarked')
      },

      /**
       * Set sort column and direction
       */
      setSort: (sort: {
        column: keyof ResponseDocumentListItem
        direction: SortOrder
      }) => {
        set({ sort }, false, 'setSort')
      },

      setLoadingTOC: (loading: boolean) => {
        set({ isLoadingTOC: loading }, false, 'setLoadingTOC')
      },

      setTOCError: (error: Error | null) => {
        set({ tocError: error }, false, 'setTOCError')
      },

      reset: () => {
        set(
          (state) => ({
            ...initialState,
            openDocuments: state.openDocuments,
            activeDocumentId: state.activeDocumentId,
            versionIdByDocumentId: state.versionIdByDocumentId,
            listVersionByDocumentId: state.listVersionByDocumentId,
          }),
          false,
          'reset'
        )
      },
    }),
    {
      name: 'DocumentStore',
    }
  )
)
